import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { IPlanoSaude } from '@app/modules/plano_saude/models/plano_saude.model';
import { PlanoSaudeService } from '@app/modules/plano_saude/services/plano_saude.service';
import {
  ConfirmDialogComponent,
  ConfirmDialogData
} from '@app/shared/components';
import { Utils } from '@app/shared/helpers/utils';
import {
  validarAdicaoRaiz,
  verificarRotasDeAcessoRaiz
} from '@app/shared/utils';
import { Dialog } from 'primeng/dialog';
import {
  CustomValidators,
  ErroMessages,
  IErroMessages,
  ScreenService,
  TokenService
} from 'viggo-sistemas-core-web';

@Component({
  templateUrl: './fast-plano-saude.component.html',
  styleUrls: ['./fast-plano-saude.component.scss'],
  providers: [PlanoSaudeService]
})
export class FastPlanoSaudeComponent implements OnInit, AfterViewInit {
  @ViewChild('fastPlanoSaude') fastPlanoSaude: Dialog;
  public form: UntypedFormGroup;

  loading = false;

  displayMessage = {
    protocolo: {
      duplicate: 'Já existe um plano de saúde com esse protocolo!',
      required: 'O protocolo é obrigatório',
      maxlength: 'Não pode ter mais de 60 caracteres',
      isEmptyValue: 'Valor inválido: remova os espaços em branco!'
    },
    descricao: {
      duplicate: 'Já existe um plano de saúde com essa descrição!',
      required: 'A descricão é obrigatória',
      maxlength: 'Não pode ter mais de 60 caracteres',
      isEmptyValue: 'Valor inválido: remova os espaços em branco!'
    },
    observacao: {
      maxlength: 'Não pode ter mais de 500 caracteres',
      isEmptyValue: 'Valor inválido: remova os espaços em branco!'
    },
    valor: {
      required: 'O valor é obrigatório',
      invalidNumber: 'Digite um número válido.',
      valueMinZero: 'Valor não pode ser zero',
      menor_valor: 'Valor menor que valor mínimo',
      notNumbers: 'Inválido: insira um número',
      before_split: 'Inválido: erro na parte inteira',
      after_split: 'Inválido: erro na parte decimal',
      errorComma: 'Inválido: excesso de virgulas',
      errorDots: 'Inválido: excesso de pontos',
      max_value: 'Inválido: passou dos limites',
      existSpecialChar: 'Valor inválido, não aceita pontuação!'
    },
    codigo: {
      required: 'O código é obrigatório',
      maxlength: 'O código deve conter no máximo 6 caracteres.'
    }
  };

  tipoDesconto = [
    {
      label: 'Usar como desconto',
      value: true
    },
    {
      label: 'Usar como valor fixo',
      value: false
    }
  ];
  tipoValor = [
    {
      label: 'Valor em porcentagem (%)',
      value: true
    },
    {
      label: 'Valor real (R$)',
      value: false
    }
  ];

  constructor(
    public dialogRef: MatDialogRef<FastPlanoSaudeComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IPlanoSaude,
    protected fb: UntypedFormBuilder,
    public entityService: PlanoSaudeService,
    public screenService: ScreenService,
    public tokenService: TokenService,
    public matSnackBar: MatSnackBar,
    public matDialog: MatDialog,
    public router: Router
  ) {}

  ngAfterViewInit(): void {
    if (this.screenService.detectDeviceInfo().isMobile)
      this.fastPlanoSaude.maximize();
  }

  ngOnInit() {
    this.form = this.fb.group({
      descricao: [
        '',
        [
          Validators.required,
          Validators.maxLength(60),
          CustomValidators.isEmptyValue()
        ]
      ],
      protocolo: [
        '',
        [
          Validators.required,
          Validators.maxLength(60),
          CustomValidators.isEmptyValue()
        ]
      ],
      observacao: [
        '',
        [Validators.maxLength(500), CustomValidators.isEmptyValue()]
      ],
      valor: [
        null,
        [
          Validators.required,
          CustomValidators.validateCurrencyValues(15, 2),
          CustomValidators.validateEqualsMinZero(),
          CustomValidators.existSpecialChar(true)
        ]
      ],
      is_porcentagem: [false, [Validators.required]],
      is_desconto: [false, [Validators.required]]
    });
    this.validateForm();
  }

  onSubmit() {
    if (this.form.valid) {
      this.loading = true;
      const { ...rest } = this.form.value;
      var entity = {
        domain_id: this.tokenService.getDomain().id,
        ...rest,
        valor: Utils.replaceCommaToDot(rest.valor)
      };
      entity = Utils.trimAttributes(entity);
      this.entityService.insert(entity).subscribe(
        plano_saude => {
          if (plano_saude) {
            this.matSnackBar.open(
              `Novo plano de saúde cadastrado com sucesso.`,
              'X',
              {
                duration: 2000,
                horizontalPosition: 'right',
                verticalPosition: 'top',
                panelClass: ['msg-success']
              }
            );
            this.dialogRef.close(plano_saude);
          }
        },
        (err: HttpErrorResponse) => {
          this.displayError(err);
          this.loading = false;
        },
        () => {
          this.loading = false;
        }
      );
    }
  }

  markAllControlsAsDirty() {
    Object.keys(this.form.controls).forEach(controlKey => {
      controlKey = String(controlKey);
      const control = this.form.get(controlKey);
      if (!control || control.valid) return;
      control.markAsDirty();
    });
  }

  validateForm() {
    this.form.markAllAsTouched();
    this.markAllControlsAsDirty();
  }

  actionFnCheckEntityExist(control: AbstractControl, field: string) {
    Utils.checkIfValueExist(
      control,
      this.entityService,
      field,
      this.tokenService.getDomain().id
    );
  }

  validateOnlyNumbers(event) {
    if (
      !/^\d+$/.test(event.key) &&
      !/[`,.]/.test(event.key) &&
      !event.ctrlKey &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete' &&
      event.key !== 'Tab' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight'
    )
      return false;
  }

  verificarDisponibilidade(url: {
    method: string;
    params?: string;
    url: string;
  }) {
    const routesAcess = verificarRotasDeAcessoRaiz(
      this.tokenService.getRoutes(),
      url.url
    );
    return validarAdicaoRaiz(routesAcess, url.method, url.params, url.url);
  }

  displayError(err: HttpErrorResponse, showDefaultMsg = false) {
    const errorMsg = new ErroMessages({
      route: this.router.url,
      router: this.router,
      matSnackBar: this.matSnackBar,
      httpError: err,
      showDefaultMsg: showDefaultMsg,
      loginService: null,
      tokenService: this.tokenService,
      dialogComponent: ConfirmDialogComponent,
      matDialog: this.matDialog,
      dialogData: {
        config: {
          data: {
            title: 'Aviso - Erro ao realizar operação',
            bodyMessage: err.error,
            icon: 'pi pi-fw pi-info-circle text-orange-500 mb-2 mr-2',
            singleBtn: true
          } as ConfirmDialogData,
          disableClose: true,
          width: '500px'
        } as MatDialogConfig<any>
      }
    } as IErroMessages);
    errorMsg.showMessageObject(err.error);
    // errorMsg.checkError();
  }
}
